// Sugalarm — Log screen.
//
// Tabs select an event_type, the body collects the right fields for
// that type, then POSTs to /api/treatments. The recent-entries strip
// shows the last 10 logged within the day and offers per-row delete.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";

import * as treatmentsApi from "../../api/treatments";
import { Button } from "../../components/ui/Button";
import { Confetti } from "../../components/ui/Confetti";
import { ConfirmDeleteModal } from "../../components/ui/ConfirmDeleteModal";
import { CongratsModal } from "../../components/ui/CongratsModal";
import { EditTreatmentModal } from "../../components/ui/EditTreatmentModal";
import { FitOrHideHint } from "./FitOrHideHint";
import { KioskNumpadOverlay } from "./KioskNumpadOverlay";
import { Pill } from "../../components/ui/Pill";
import { ViewEntryModal } from "../../components/ui/ViewEntryModal";
import { InsulinFlow } from "./InsulinFlow";
import {
  discardBolusUnits,
  readBolusPhotoHandoff,
  type BolusPhotoHandoff,
} from "./bolusPhotoHandoff";
import { treatmentColor } from "../../components/glucose/markerGlyphs";
import { apiUrl } from "../../env";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { useIsLandscape } from "../../hooks/useIsLandscape";
import { isAndroidTabletClassRuntime } from "../../lib/deviceClass";
import { useSettings } from "../../hooks/useSettings";
import { hapticSelect, hapticSuccess } from "../../lib/haptics";
import { ACCEPT_IMAGE_CAMERA, ACCEPT_IMAGE_GALLERY } from "../../lib/imageAccept";
import { CARBS_INVALID_MESSAGE, parseCarbGrams } from "../../lib/logEntryValidation";
import {
  openPhotoInput,
  photoBlockedByAlarm,
  PHOTO_BLOCKED_LINE,
  usePhotoBlockedByAlarm,
} from "../../lib/nativeCamera";
import { cameraOnlyDevice } from "../../lib/photoSource";
import { setPendingPhotoFile } from "../../lib/photoHandoff";
import { fmtEntryTime, fmtTime, localDayKey } from "../../lib/glucose";
import type { Treatment, TreatmentCreateIn } from "../../lib/types";
import styles from "./LogScreen.module.css";

type Kind = "carbs" | "insulin" | "bg_check" | "note" | "exercise";

// Cycle 126: Insulin leads the type selector (it's the most frequent,
// dose-first entry). Display-order only — the Postgres event_type enum is
// unchanged. The same order is mirrored on the QuickLogRow tiles and the
// /log/recent filter chips.
const TABS: { id: Kind; label: string; sub: string }[] = [
  { id: "insulin", label: "Insulin", sub: "u" },
  { id: "carbs", label: "Carbs", sub: "g" },
  { id: "bg_check", label: "BG", sub: "mg/dL" },
  { id: "note", label: "Note", sub: "" },
  { id: "exercise", label: "Exercise", sub: "min" },
];

/** The tab Log opens on for a `?tab=`/`?kind=` value. Allowlisted against
 *  TABS; `bg` is the legacy QuickLogRow alias; anything else — including no
 *  value — is Insulin, the first tab. */
export function initialLogKind(raw: string | null): Kind {
  if (raw === "bg") return "bg_check";
  return TABS.find((t) => t.id === raw)?.id ?? "insulin";
}

/** The tab ids in rendered order (the single source both render paths map). */
export const LOG_TAB_IDS: readonly Kind[] = TABS.map((t) => t.id);

const LABEL_PLACEHOLDER: Record<Exclude<Kind, "note">, string> = {
  carbs: "Apple, lunch, etc.",
  insulin: "Bolus, basal, correction…",
  bg_check: "Fingerstick, post-meal…",
  exercise: "Walk, gym, swim…",
};

const TIME_OFFSETS_MIN: { id: string; label: string; offset: number | "pick" }[] = [
  { id: "now", label: "now", offset: 0 },
  { id: "5", label: "5m ago", offset: 5 },
  // Cycle 115: 10m added (used by the landscape "when" preset chips; it
  // also appears in the portrait dropdown — harmless, an extra choice).
  { id: "10", label: "10m ago", offset: 10 },
  { id: "15", label: "15m ago", offset: 15 },
  { id: "30", label: "30m ago", offset: 30 },
  { id: "60", label: "1h ago", offset: 60 },
  { id: "pick", label: "pick…", offset: "pick" },
];

// Cycle 115: the landscape Log "when" preset chips — short labels, the
// subset the mock specifies. Each id maps to a TIME_OFFSETS_MIN entry so
// `timestampIso` resolves them unchanged.
const LANDSCAPE_WHEN: { id: string; label: string }[] = [
  { id: "now", label: "Now" },
  { id: "5", label: "5m" },
  { id: "10", label: "10m" },
  { id: "15", label: "15m" },
  { id: "30", label: "30m" },
];

function emptyValueForKind(_kind: Kind): string {
  return "";
}

function kindNeedsNumpad(kind: Kind): boolean {
  return kind === "carbs" || kind === "insulin" || kind === "bg_check" || kind === "exercise";
}

function kindLabel(kind: Kind): string {
  return TABS.find((t) => t.id === kind)?.label ?? kind;
}

function kindUnit(kind: Kind, units: "mg/dL" | "mmol/L"): string {
  if (kind === "bg_check") return units;
  return TABS.find((t) => t.id === kind)?.sub ?? "";
}

// Cycle 34.19: build the personalized "Congratulations!" message body.
// Used by the mobile log success path; renders inside CongratsModal
// under the "Congratulations!" title. The body is a single sentence so
// the modal stays compact.
function buildCongratsMessage(
  kind: Kind,
  rawValue: string,
  rawNotes: string,
  units: "mg/dL" | "mmol/L",
): string {
  const notes = rawNotes.trim();
  const value = rawValue.trim();
  switch (kind) {
    case "carbs":
      return notes
        ? `You logged ${notes} with ${value}g of carbs.`
        : `You logged ${value}g of carbs.`;
    case "insulin": {
      const n = Number(value);
      const word = Number.isFinite(n) && n === 1 ? "unit" : "units";
      return notes
        ? `You logged ${value} ${word} of insulin (${notes}).`
        : `You logged ${value} ${word} of insulin.`;
    }
    case "bg_check":
      return `You logged a BG check of ${value} ${units}.`;
    case "exercise":
      return notes
        ? `You just logged ${value} min of ${notes}.`
        : `You just logged ${value} min of exercise.`;
    case "note":
      return "You just created a note entry.";
  }
}

function toLocalInputValue(d: Date): string {
  // datetime-local takes "YYYY-MM-DDTHH:mm"
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** Cycle 452: Log rendered as an overlay on `/now` while alarm audio is
 *  sounding, instead of as its own route. Absent → the `/log` route, exactly
 *  as before. */
export interface LogOverlayMode {
  /** The intent the Now affordance would have navigated with — the query
   *  part of `/log?…` (`""` for bare Log, `"?tab=insulin&basal=1"` for the
   *  basal path). Read in place of the URL; the URL is never touched. */
  search: string;
  /** Sustained alarm audio is sounding right now (the same value the
   *  route rule pins on). Only chooses the photo line's wording. */
  alarmSounding: boolean;
}

/** Cycle 452: the line that stands where photo entry would be, inside the
 *  overlay. Photo capture is its own route (`/log/photo`), and a route change
 *  leaves `/now` — the pin would bounce it while the alarm sounds, and once
 *  it is quiet it would still drop the entry in progress. So photo returns
 *  after the overlay is closed, and the line says so. */
export function overlayPhotoLine(alarmSounding: boolean): string {
  return alarmSounding
    ? "Photo logging is available once the alarm is quiet."
    : "Close this to log with a photo.";
}

interface LogScreenProps {
  variant: FrameVariant;
  /** Cycle 452: present only when Log is opened over `/now`. */
  overlay?: LogOverlayMode;
}

export function LogScreen({ variant, overlay }: LogScreenProps) {
  const { units, settings } = useSettings();
  const h24 = settings?.display_24h ?? false;
  const isLandscape = useIsLandscape();
  const mobileLandscape = variant === "mobile" && isLandscape;
  // Cycle 199: the Android-tablet kiosk uses the MOBILE native-input path
  // (native <input inputmode> → OS number pad) instead of the custom
  // Numpad + KioskNumpadOverlay slide-in. The Pi kiosk keeps the overlay.
  const nativeEntry = variant === "kiosk" && isAndroidTabletClassRuntime();
  // Cycle 203: fire the confetti + congrats celebration on the Android-tablet
  // kiosk too — it was mobile-only (the kiosk never celebrated a log). The
  // Pi/iPad/web kiosk (marker absent) stay non-celebrating.
  const celebrate = variant === "mobile" || nativeEntry;
  const [urlParams] = useSearchParams();
  // Cycle 452: in the overlay the intent comes from the prop, never the URL
  // (the URL is `/now`, and the overlay adds no URL state).
  const overlaySearch = overlay?.search;
  const params = useMemo(
    () =>
      overlaySearch != null ? new URLSearchParams(overlaySearch) : urlParams,
    [overlaySearch, urlParams],
  );
  const nav = useNavigate();
  // Cycle 121: the basal-line "Log …" affordance deep-links to
  // `/log?tab=insulin&basal=1`. `tab` is an alias for `kind` (so the
  // existing `?kind=` links keep working), and `basal=1` pre-enables the
  // Long-acting toggle.
  // Cycle 451.2: with no intent (or an unknown one) Log opens on Insulin,
  // the first tab — it was "carbs", a leftover from before cycle 126 put
  // Insulin first. The raw value is only ever compared against the fixed
  // tab ids, never rendered.
  const rawKind = params.get("tab") ?? params.get("kind");
  const safeInitial: Kind = initialLogKind(rawKind);
  const basalParam = params.get("basal") === "1";

  const [kind, setKind] = useState<Kind>(safeInitial);
  // Cycle 126: deep link `/log?tab=insulin&basal=1` opens the guided
  // Basal path directly (the InsulinFlow component owns the basal entry
  // now — the cycle-121 toggle UI is replaced).
  const initialBasal = basalParam && safeInitial === "insulin";
  // Cycle 126: photo-return payload from the Bolus food step's photo
  // estimator (`/log/photo` hands the chosen carbs + names back rather
  // than saving a standalone carbs row). Consumed once by InsulinFlow.
  // Cycle 451.5: read from route state HERE, at mount, so InsulinFlow mounts
  // with it in hand. It was set by the arrival effect a render later, after
  // InsulinFlow's state initializers had already opened the path chooser.
  // Only for the Insulin tab; InsulinFlow reports it consumed and it is
  // dropped (onPhotoReturnConsumed below).
  const location = useLocation();
  const [bolusPhotoReturn, setBolusPhotoReturn] =
    useState<BolusPhotoHandoff | null>(() =>
      // Cycle 452: the overlay never carries a photo handoff — photo entry
      // is not offered inside it, and `/now`'s route state is not Log's.
      safeInitial === "insulin" && overlay == null
        ? readBolusPhotoHandoff(location.state)
        : null,
    );
  const onPhotoReturnConsumed = useCallback(() => setBolusPhotoReturn(null), []);
  // Cycle 451.5: a fresh Log mount means any typed units still armed for the
  // photo screen belong to a flow that was abandoned (Back, Forward, reload).
  useEffect(() => {
    discardBolusUnits();
  }, []);
  const [value, setValue] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [tsChip, setTsChip] = useState<string>("now");
  const [pickedAt, setPickedAt] = useState<string>(() =>
    toLocalInputValue(new Date()),
  );
  const [recent, setRecent] = useState<Treatment[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [photoServerEnabled, setPhotoEnabled] = useState<boolean>(false);
  // Cycle 452: inside the overlay no photo entry point is offered (photo is
  // its own route); a line stands in its place instead.
  const photoEnabled = overlay == null && photoServerEnabled;
  const photoLine =
    overlay != null && photoServerEnabled
      ? overlayPhotoLine(overlay.alarmSounding)
      : null;
  // Cycle 452: links that leave for `/log/recent` are not offered in the
  // overlay either — while the alarm sounds the pin would bounce them, and a
  // control that silently does nothing is not shippable (cycle 451.1).
  const offerRouteLinks = overlay == null;
  // Cycle 34.18: incremented on every successful mobile log to fire a
  // celebratory confetti burst. Mobile only — the kiosk + web frames
  // don't render the <Confetti /> consumer at all.
  const [confettiBurst, setConfettiBurst] = useState<number>(0);
  // Cycle 34.19: personalized congrats body shown inside CongratsModal.
  // Null when the modal isn't visible. Mobile-only consumer.
  const [congratsMsg, setCongratsMsg] = useState<string | null>(null);
  // Cycle 34.20: row currently being edited via EditTreatmentModal. The
  // modal closes itself; its `onSaved` callback nudges reloadRecent so
  // the local cache picks up the new fields even before the cycle-24
  // WS frame round-trips.
  const [editingRow, setEditingRow] = useState<Treatment | null>(null);
  // Cycle 34.22: row currently being viewed (read-only) via
  // ViewEntryModal. Tap an entry row to see its full notes.
  const [viewingRow, setViewingRow] = useState<Treatment | null>(null);
  // Cycle 42: row pending delete confirmation. The `×` no longer
  // deletes directly — it opens <ConfirmDeleteModal>; removeOne only
  // fires when the user confirms.
  const [pendingDelete, setPendingDelete] = useState<Treatment | null>(null);
  // Cycle 133: kiosk-only — the numpad is a slide-in overlay over the
  // Today's-entries card. `padHost` is the overflow-clipped portal target
  // overlaying that card; `kioskPadOpen` drives the generic (Carbs/BG/
  // Exercise) pad's slide. The Insulin tab's pad is owned by InsulinFlow,
  // portaled into the same host.
  const [padHost, setPadHost] = useState<HTMLElement | null>(null);
  const [kioskPadOpen, setKioskPadOpen] = useState<boolean>(false);

  // Cycle 32: surface the photo-meal flow's confirm message handed off
  // via location.state. Consume + clear so a back/forward navigation
  // doesn't re-show the pill. Cycle 34.18: a photo-flow handoff also
  // fires the confetti burst on the mobile path so the user gets the
  // same celebratory feedback as the inline log paths.
  useEffect(() => {
    // Cycle 452: the overlay is not an arrival — `/now`'s route state is
    // not a Log handoff, and replacing `/now`'s history entry here would
    // close the overlay (it is keyed on that entry).
    if (overlay != null) return;
    const st = location.state as
      | { confirm?: string; congrats?: string; bolusPhoto?: unknown }
      | null;
    // Cycle 126: the Bolus food step's photo estimator hands its result
    // back here (not a standalone carbs save). Cycle 451.5: the payload was
    // already read at mount (above); this only clears it from the history
    // entry, so a reload or Forward never sees it again.
    if (st && "bolusPhoto" in st) {
      nav(location.pathname + location.search, { replace: true, state: null });
      return;
    }
    const msg = st?.confirm;
    if (!msg) return;
    // Cycle 451.4: web and kiosk show the confirmation pill; phones show
    // only their congrats pop-up (the pill never survived arrival there, so
    // setting it now would show both).
    if (variant !== "mobile") setConfirm(msg);
    if (variant === "mobile") {
      setConfettiBurst((n) => n + 1);
      // Cycle 34.19: photo-meal handoff carries an optional richer
      // `congrats` body (e.g. "You logged Cheeseburger with 60 g of
      // carbs."). Fall back to the inline confirm copy if the
      // upstream surface didn't include one.
      setCongratsMsg(st?.congrats ?? msg);
    }
    nav(location.pathname + location.search, {
      replace: true,
      state: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cycle 451.4: a confirmation belongs to the visit that produced it. Back
  // (a back navigation) or a push to another Log entry keeps this screen
  // mounted, so any move to a new history entry clears it — even one with
  // the same URL. The arrival handoff's own REPLACE is the one move that
  // keeps it.
  const navType = useNavigationType();
  const confirmEntry = useRef(location.key);
  useEffect(() => {
    if (confirmEntry.current === location.key) return;
    confirmEntry.current = location.key;
    if (navType !== "REPLACE") setConfirm(null);
  }, [location.key, navType]);

  // Cycle 27: probe the server's photo feature flag once on mount. The
  // button hides until the response arrives — false-by-default avoids
  // flashing an unsupported control.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl("/api/health"));
        if (!res.ok) return;
        const data = (await res.json()) as {
          photo?: { enabled?: boolean };
        };
        if (!cancelled) setPhotoEnabled(Boolean(data.photo?.enabled));
      } catch {
        /* ignore — feature simply stays hidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reset numpad value and per-tab text fields when switching tabs.
  // Cycle 126: the Insulin tab is owned by <InsulinFlow>, which resets its
  // own in-progress state on unmount (i.e. when the user leaves the tab),
  // so there is nothing basal-specific to reset here anymore.
  // Cycle 451.4: only on a real CHANGE of tab. On arrival every field is
  // already at its initial value, and running the reset there cleared the
  // photo-log confirmation the arrival effect had just set (since cycle 32).
  // Keyed on the previous kind, not a first-run flag, so StrictMode's second
  // effect pass is a no-op too.
  const resetForKind = useRef(kind);
  useEffect(() => {
    if (resetForKind.current === kind) return;
    resetForKind.current = kind;
    setValue(emptyValueForKind(kind));
    setError(null);
    setConfirm(null);
    setNotes("");
    // Cycle 133: switching entry type slides the kiosk numpad back out.
    setKioskPadOpen(false);
    // Cycle 126: leaving Insulin clears any consumed photo-return payload.
    if (kind !== "insulin") setBolusPhotoReturn(null);
  }, [kind]);

  const reloadRecent = useCallback(async () => {
    const r = await treatmentsApi.list(24, 10);
    if (r.ok) setRecent(r.data.treatments);
  }, []);

  useEffect(() => {
    void reloadRecent();
  }, [reloadRecent]);

  const timestampIso = useMemo(() => {
    if (tsChip === "pick") {
      // datetime-local is parsed in the user's local TZ; turning it into
      // an ISO string emits UTC.
      const local = new Date(pickedAt);
      if (Number.isNaN(local.getTime())) return new Date().toISOString();
      return local.toISOString();
    }
    const def = TIME_OFFSETS_MIN.find((t) => t.id === tsChip);
    const offset = typeof def?.offset === "number" ? def.offset : 0;
    return new Date(Date.now() - offset * 60_000).toISOString();
  }, [tsChip, pickedAt]);

  // Cycle 115: muted resolved-time line for the landscape "when" chips,
  // e.g. `logs at 1:39 PM · 10 min ago`. Honors display_24h via fmtTime.
  const resolvedWhen = useMemo(() => {
    const t = fmtTime(new Date(timestampIso), { h24 });
    if (tsChip === "now") return `logs at ${t} · now`;
    if (tsChip === "pick") return `logs at ${t}`;
    const def = TIME_OFFSETS_MIN.find((x) => x.id === tsChip);
    const off = typeof def?.offset === "number" ? def.offset : 0;
    return `logs at ${t} · ${off} min ago`;
  }, [timestampIso, tsChip, h24]);

  const submit = async () => {
    setError(null);
    setConfirm(null);

    const body: TreatmentCreateIn = {
      event_type: kind,
      timestamp: timestampIso,
    };

    const trimmed = notes.trim();

    if (kind === "carbs") {
      // 0 is valid — cheese, eggs, meat, side salad: zero-carb foods
      // are worth recording so the photo estimator's "negligible
      // carbs" items can still be saved. Cycle 451.5: the check is shared
      // with the meal-photo bolus handoff.
      const n = parseCarbGrams(value);
      if (n === null) {
        setError(CARBS_INVALID_MESSAGE);
        return;
      }
      body.carbs_g = n;
      if (trimmed) body.notes = trimmed;
      // Cycle 126: the Insulin tab is handled entirely by <InsulinFlow>
      // (the guided Bolus / Correction / Basal paths own their own
      // submit), so this generic submit never runs for kind === "insulin".
    } else if (kind === "bg_check") {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) {
        setError(`Enter glucose in ${units}.`);
        return;
      }
      // BG checks have no dedicated column in the existing schema —
      // store the value in `notes` so it's still searchable, plus a
      // small marker so future cycles can migrate cleanly. The wire
      // is always mg/dL (the user types in their preferred unit, but
      // we record the absolute scale alongside the value so it can't
      // be misread later).
      body.notes = `BG ${n} ${units}${trimmed ? ` — ${trimmed}` : ""}`;
    } else if (kind === "exercise") {
      const n = Number(value);
      if (!Number.isFinite(n) || n <= 0) {
        setError("Enter duration in minutes.");
        return;
      }
      body.notes = `${n} min${trimmed ? ` — ${trimmed}` : ""}`;
    } else if (kind === "note") {
      if (!trimmed) {
        setError("Add a note.");
        return;
      }
      body.notes = trimmed;
    }

    setBusy(true);
    const r = await treatmentsApi.create(body);
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    // Build the personalized congrats body BEFORE we clear value /
    // notes — those inputs are the message source.
    const congratsBody = celebrate
      ? buildCongratsMessage(kind, value, notes, units)
      : null;
    setValue("");
    setNotes("");
    setKioskPadOpen(false); // Cycle 133: slide the pad out on a log.
    setConfirm(`Logged ${kindLabel(kind).toLowerCase()}.`);
    // Cycle 203: celebrate on mobile AND the Android-tablet kiosk.
    if (celebrate) {
      setConfettiBurst((n) => n + 1);
      setCongratsMsg(congratsBody);
    }
    // Cycle 115: a successful log fires a success haptic and (landscape
    // only) resets the "when" chip back to Now. Portrait keeps its
    // dropdown selection (byte-identical). Haptics stay mobile-only — a
    // wall-mounted kiosk shouldn't buzz.
    if (variant === "mobile") hapticSuccess();
    if (mobileLandscape) setTsChip("now");
    void reloadRecent();
  };

  // Cycle 126: <InsulinFlow> owns the Insulin tab's submit; this fires the
  // same post-log feedback the generic submit does (confirm pill + mobile
  // confetti/congrats + recent reload + landscape "when" reset).
  const onInsulinLogged = useCallback(
    (msg: { confirm: string; congrats: string | null }) => {
      setConfirm(msg.confirm);
      // Cycle 203: celebrate on mobile AND the Android-tablet kiosk.
      if (celebrate) {
        setConfettiBurst((n) => n + 1);
        setCongratsMsg(msg.congrats);
      }
      if (variant === "mobile") hapticSuccess();
      if (mobileLandscape) setTsChip("now");
      void reloadRecent();
    },
    [variant, celebrate, mobileLandscape, reloadRecent],
  );

  const removeOne = async (id: string) => {
    const r = await treatmentsApi.remove(id);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    void reloadRecent();
  };

  // Cycle 34: kiosk right column shows today's entries only (filtered
   // by local calendar day via `localDayKey`); web keeps the cycle-29
   // rolling 24-hour list as-is. Reuses the existing 24-hour fetch so
   // there's no extra round-trip — the filter is client-side.
  const visibleEntries = useMemo(() => {
    if (variant !== "kiosk") return recent;
    const todayKey = localDayKey(Date.now());
    return recent.filter((tr) => localDayKey(tr.timestamp) === todayKey);
  }, [recent, variant]);

  // Cycle 43: the cycle-42 `?focus=<treatment_id>` scroll-into-view +
  // `.rowPulse` handler is retired. Its only caller was the cycle-42
  // ViewEntryModal "Edit in Log ›" button, which cycle 43 removed in
  // favor of the inline <ActiveBolusesModal> editor on the Now screen
  // (the user never has to leave Now to edit a bolus). No reachable
  // path threads `?focus=` anymore; the param is now a silent no-op.
  // `.rowPulse` stays in LogScreen.module.css (cheap, future-proof).

  const useNumpad = kindNeedsNumpad(kind);
  const noteSection = kind === "note";
  // Cycle 32: the inline photo card is gone; the CTA(s) below hand a File
  // straight to the dedicated /log/photo flow, which saves directly. Same
  // kiosk + health-flag gates as cycle 27.
  // Cycle 200: un-gate on the Android tablet kiosk only (it has a rear
  // camera + native in-app capture). The Pi kiosk and the iPad kiosk
  // stay excluded; web/mobile unchanged. Sits alongside the cycle-199
  // `nativeEntry` Carbs input, not the retired kiosk numpad overlay.
  const showPhotoButton =
    kind === "carbs" &&
    (variant !== "kiosk" || isAndroidTabletClassRuntime()) &&
    photoEnabled;
  // Cycle 452: the overlay's stand-in, on exactly the surfaces and tab where
  // the photo button would have been.
  const photoLineNode =
    photoLine != null &&
    kind === "carbs" &&
    (variant !== "kiosk" || isAndroidTabletClassRuntime()) ? (
      <p className={styles.overlayPhotoLine} data-testid="log-overlay-photo-line">
        {photoLine}
      </p>
    ) : null;

  // Cycle 202: Android-tablet kiosk — the prominent Carbs CTA opens the
  // photo chooser DIRECTLY (no dead "/log/photo" landing page). The
  // file-input click must fire inside the user's tap gesture (Android
  // WebView blocks input.click() from a mount effect for lack of a user
  // gesture), so the CTA's onClick drives this hidden input. On a picked
  // image we hand the File to PhotoMealScreen via the module holder (NOT
  // through history state) and route to /log/photo, which enters at the
  // `preview` phase. On chooser cancel no file arrives → we stay on the
  // Carbs tab. The blob-URL lifecycle is owned by PhotoMealScreen.
  const photoInputRef = useRef<HTMLInputElement>(null);
  const showTabletPhotoCta = nativeEntry && showPhotoButton;
  // Cycle 458: the kiosk APK is camera-only (lib/photoSource). Its CTA input
  // carries `capture="environment"` so a chooser whose cycle-455 purpose
  // never paired can route only to the in-app camera (FileChooserParams
  // .isCaptureEnabled → never the "Choose from gallery" dialog). The standard
  // phone app on a tablet keeps the capture-less input, which is what reaches
  // its photo picker. Gallery buttons are not rendered on the kiosk APK.
  const mealCameraOnly = cameraOnlyDevice();
  // Cycle 455: the purpose goes to the kiosk APK right before the click
  // (meal_photo → the rear camera directly), and while any alarm sounds on
  // the kiosk the button is disabled (lib/nativeCamera).
  const photoBlocked = usePhotoBlockedByAlarm();
  const openTabletPhotoChooser = useCallback(() => {
    if (photoBlockedByAlarm()) return;
    openPhotoInput(photoInputRef.current, "meal_photo");
  }, []);
  const onTabletPhotoPicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Clear so re-picking the same file still fires change.
      if (photoInputRef.current) photoInputRef.current.value = "";
      if (!file) return; // chooser cancelled → stay on the Carbs tab
      setPendingPhotoFile(file);
      nav("/log/photo");
    },
    [nav],
  );

  // Cycle 340: web / iOS / Android-phone meal-photo entry — two explicit
  // targets (camera vs library), each firing its hidden input directly
  // inside the tap gesture and handing the File straight to PhotoMealScreen
  // via the same handoff module the tablet CTA above uses, so every
  // platform enters at the `preview` phase with zero extra screens/clicks.
  // The Android-tablet kiosk keeps the single-button block above — its
  // native WebChromeClient already turns one no-capture click into its own
  // "Take photo / Choose from gallery" dialog, and giving it two explicit
  // capture-direction inputs instead would need a native (APK) change.
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const openCamera = useCallback(() => {
    openPhotoInput(cameraInputRef.current, "meal_photo");
  }, []);
  const openGallery = useCallback(() => {
    if (mealCameraOnly) return;
    galleryInputRef.current?.click();
  }, [mealCameraOnly]);
  const onDirectPhotoPicked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return; // chooser cancelled
      setPendingPhotoFile(file);
      nav("/log/photo");
    },
    [nav],
  );

  // Cycle 126: the shared "when" backdate control, built once per layout
  // and handed to <InsulinFlow> (so the timestamp control stays present
  // on every guided Insulin path) as well as the generic non-insulin
  // entry. Landscape = the short chip row + a resolved-time line; the
  // other layouts = the cycle-57.11 dropdown.
  const whenDropdown = (
    <label className={styles.timeSelectRow}>
      <span className={styles.timeSelectLabel}>When</span>
      <select
        value={tsChip}
        onChange={(e) => setTsChip(e.target.value)}
        className={styles.timeSelect}
        aria-label="When"
      >
        {TIME_OFFSETS_MIN.map((t) => (
          <option key={t.id} value={t.id}>
            {t.label}
          </option>
        ))}
      </select>
      {tsChip === "pick" && (
        <input
          type="datetime-local"
          value={pickedAt}
          onChange={(e) => setPickedAt(e.target.value)}
          className={styles.timeInput}
        />
      )}
    </label>
  );
  // Cycle 327: unified mobile "when" chip strip (portrait + landscape) —
  // Now · 5m · 10m · 15m · 30m, plus a `pick…` chip that reveals the native
  // datetime-local. The strip replaces the portrait `whenDropdown`; the
  // `pick…` path PRESERVES backdating beyond the presets (the dropdown's
  // `1h ago` + custom `pick…`) so that capability is not silently dropped.
  const whenChips = (
    <div className={styles.whenBlock}>
      <div className={styles.lsWhenRow}>
        {LANDSCAPE_WHEN.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => {
              hapticSelect();
              setTsChip(w.id);
            }}
            className={`${styles.lsWhenChip} ${
              tsChip === w.id ? styles.lsWhenChipActive : ""
            }`}
          >
            {w.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            hapticSelect();
            setTsChip("pick");
          }}
          className={`${styles.lsWhenChip} ${
            tsChip === "pick" ? styles.lsWhenChipActive : ""
          }`}
        >
          pick…
        </button>
      </div>
      {tsChip === "pick" && (
        <input
          type="datetime-local"
          value={pickedAt}
          onChange={(e) => setPickedAt(e.target.value)}
          className={styles.timeInput}
        />
      )}
      <div className={styles.lsWhenResolved}>{resolvedWhen}</div>
    </div>
  );
  // Cycle 327: both mobile orientations use the chip strip; kiosk/web keep
  // the cycle-57.11 dropdown.
  const insulinWhenControl = variant === "mobile" ? whenChips : whenDropdown;

  // Cycle 126: mobile numeric entry uses the native OS number pad — a
  // native <input inputmode> raises the OS keyboard on focus. The custom
  // <Numpad> is gone from the mobile Carbs / BG / Exercise layouts (and
  // the Insulin flow uses its own native input). Carbs / BG / Exercise
  // are integer-valued here, so `inputmode="numeric"`.
  const mobileNumericInput = (
    <input
      type="text"
      className={styles.mobileNumInput}
      inputMode="numeric"
      placeholder="—"
      value={value}
      onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ""))}
      maxLength={kind === "bg_check" ? 3 : 5}
      aria-label={kindUnit(kind, units) || "value"}
    />
  );

  // Cycle 451.4: the tablet kiosk's Exercise duration field — the same
  // native input plus the unit beside it ("min", the copy every other
  // layout shows). The tablet layout only; phones keep mobileNumericInput.
  // Cycle 452.1: Carbs ("g") and BG (the member's configured glucose unit,
  // from settings — never a literal) use the same field, at the same 14px.
  const tabletUnitField = (
    <div className={styles.unitField}>
      {mobileNumericInput}
      <span className={styles.unitFieldLabel}>{kindUnit(kind, units)}</span>
    </div>
  );

  // Cycle 293: web numeric entry uses the OS keyboard — a native <input>
  // replaces the custom <Numpad> "calculator" on the desktop/web Log. Carbs
  // allow a decimal; BG / exercise are integer-valued.
  const webNumericInput = (
    <div className={styles.webAmountRow}>
      <input
        type="text"
        className={styles.webNumInput}
        inputMode={kind === "carbs" ? "decimal" : "numeric"}
        placeholder="—"
        value={value}
        onChange={(e) =>
          setValue(
            e.target.value.replace(kind === "carbs" ? /[^0-9.]/g : /[^0-9]/g, ""),
          )
        }
        maxLength={kind === "bg_check" ? 3 : 5}
        aria-label={kindUnit(kind, units) || "value"}
      />
      {kindUnit(kind, units) ? (
        <span className={styles.webNumUnit}>{kindUnit(kind, units)}</span>
      ) : null}
    </div>
  );

  // Cycle 126: the guided Insulin flow (Bolus / Correction / Basal). It
  // owns the entire Insulin-tab entry + submit + its own numeric input
  // (native on mobile, the shared <Numpad> on kiosk/web). Mounted in
  // place of the generic entry whenever the Insulin tab is active.
  const insulinFlow = (
    <InsulinFlow
      variant={variant}
      isLandscape={isLandscape}
      units={units}
      h24={h24}
      photoEnabled={photoEnabled}
      photoNotice={photoLine}
      timestampIso={timestampIso}
      whenControl={insulinWhenControl}
      initialBasal={initialBasal}
      photoReturn={bolusPhotoReturn}
      onPhotoReturnConsumed={onPhotoReturnConsumed}
      onLogged={onInsulinLogged}
      kioskPadPortal={padHost}
    />
  );
  const isInsulin = kind === "insulin";

  // Cycle 451.3: the Exercise field's label + duration row splits its width
  // AFTER the gap, so it no longer runs 8px past its card. Cycle 451.4: the
  // Carbs and BG rows take the same split (their own class, same rule).
  const entryRowClass =
    kind === "exercise"
      ? `${styles.entryTopRow} ${styles.entryTopRowExercise}`
      : kind === "carbs" || kind === "bg_check"
        ? `${styles.entryTopRow} ${styles.entryTopRowSplit}`
        : styles.entryTopRow;

  // Cycle 201: the kiosk type chips, extracted so the Android-tablet kiosk
  // can render the segmented selector full-width (above both columns) while
  // the Pi/web kiosk renders the identical chips inside the left column.
  const kioskTabs = TABS.map((t) => (
    <button
      key={t.id}
      type="button"
      onClick={() => setKind(t.id)}
      className={`${styles.tab} ${kind === t.id ? styles.tabActive : ""}`}
    >
      {t.label}
      {/* Cycle 451.3: the Exercise hint is shown whole or not at all — on the
          narrow kiosk column it has no room and used to render as a clipped
          fragment. Cycle 452.1: EVERY tab's hint follows that rule and sits
          at the 13px type floor (the others were 11px). Tab widths, order,
          labels and the Insulin-first default are unchanged. */}
      {t.sub ? (
        <FitOrHideHint style={{ color: "var(--ink-3)", marginLeft: 6, fontSize: 13 }}>
          {t.sub}
        </FitOrHideHint>
      ) : null}
    </button>
  ));

  // ─── Cycle 327: shared mobile building blocks (portrait + landscape) ───

  // Kiosk-style segmented type menu with unit hints. One translucent
  // segmented strip of five equal segments — Insulin u · Carbs g ·
  // BG mg/dL · Note · Exercise min. The active segment is filled with
  // --accent (white label + softened hint); inactive segments are
  // transparent with muted ink. Label + hint stack so nothing clips at
  // 390px. Selecting a segment swaps the type exactly as before — the panes
  // and selection behavior are unchanged. Replaces `.mobileTabs`.
  const segMenu = (
    <div className={styles.segMenu} role="tablist" aria-label="Entry type">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={kind === t.id}
          onClick={() => {
            hapticSelect();
            setKind(t.id);
          }}
          className={`${styles.segItem} ${
            kind === t.id ? styles.segItemActive : ""
          }`}
        >
          <span className={styles.segLabel}>{t.label}</span>
          {/* Always render the hint line — a non-breaking space for Note
              (which has no unit) — so every label sits on the same top line;
              Note stays balanced with the other segments instead of
              vertically centering its lone word. */}
          <span className={styles.segHint}>{t.sub || " "}</span>
        </button>
      ))}
    </div>
  );

  // The mobile input region for the selected type. Carbs (deliverable 3):
  // Take Photo / Choose from Library CTAs first (cycle 340: each opens the
  // camera/gallery directly, then hands the File straight to the
  // meal-photo → carbs flow), then an OR ENTER MANUALLY divider, then the
  // meal description (¾) + grams (¼, trailing `g`) on one line. The other
  // four panes keep their existing inputs. Only evaluated on the mobile path.
  const mobileInputs = noteSection ? (
    <textarea
      className={styles.notes}
      placeholder="What happened?"
      value={notes}
      onChange={(e) => setNotes(e.target.value)}
    />
  ) : kind === "carbs" ? (
    <>
      {photoLineNode}
      {showPhotoButton && (
        <>
          <div className={styles.mobilePhotoCtaGroup}>
            <button
              type="button"
              className={styles.mobilePhotoCta}
              onClick={openCamera}
            >
              <span className={styles.mobilePhotoCtaIcon} aria-hidden>
                📷
              </span>
              <span className={styles.mobilePhotoCtaText}>
                <span className={styles.mobilePhotoCtaTitle}>Take Photo</span>
                <span className={styles.mobilePhotoCtaHint}>
                  Snap it — we'll estimate the carbs
                </span>
              </span>
            </button>
            {mealCameraOnly ? null : (
              <button
                type="button"
                className={styles.mobilePhotoCtaSecondary}
                onClick={openGallery}
              >
                <span aria-hidden>🖼️</span>
                Choose from Library
              </button>
            )}
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept={ACCEPT_IMAGE_CAMERA}
            capture="environment"
            onChange={onDirectPhotoPicked}
            style={{ display: "none" }}
          />
          {mealCameraOnly ? null : (
            <input
              ref={galleryInputRef}
              type="file"
              accept={ACCEPT_IMAGE_GALLERY}
              onChange={onDirectPhotoPicked}
              style={{ display: "none" }}
            />
          )}
          <div className={styles.mobileOrDivider}>
            <span>or enter manually</span>
          </div>
        </>
      )}
      <div className={`${styles.entryTopRow} ${styles.entryTopRowSplit}`}>
        <input
          type="text"
          className={styles.labelInput}
          placeholder={LABEL_PLACEHOLDER.carbs}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={120}
        />
        <div className={styles.mobileGrams}>
          <input
            type="text"
            className={styles.mobileGramsInput}
            inputMode="numeric"
            placeholder="—"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ""))}
            maxLength={5}
            aria-label="grams"
          />
          <span className={styles.mobileGramsUnit}>g</span>
        </div>
      </div>
    </>
  ) : (
    <div className={entryRowClass}>
      <input
        type="text"
        className={styles.labelInput}
        placeholder={LABEL_PLACEHOLDER[kind as Exclude<Kind, "note">]}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        maxLength={120}
      />
      {mobileNumericInput}
    </div>
  );

  // The mobile log-form card contents. Insulin keeps its guided flow (owns
  // its own Save inside the card). Every other type: its inputs, the "when"
  // chip strip, inline error/confirm, and the primary submit CTA — all
  // grouped INSIDE the translucent card (deliverable 2).
  const mobileCardBody = isInsulin ? (
    insulinFlow
  ) : (
    <>
      {mobileInputs}
      {whenChips}
      {error && <div className={styles.errorMsg}>{error}</div>}
      {confirm && <Pill tone="ok">{confirm}</Pill>}
      <Button
        onClick={submit}
        disabled={busy}
        full
        className={styles.mobileCardSubmit}
      >
        {busy ? "Logging…" : `Log ${kindLabel(kind)}`}
      </Button>
    </>
  );

  // ─── Cycle 327: mobile LANDSCAPE — the SAME single kiosk-style column as
  // portrait. The Cycle-115 two-pane split is retired: a segmented menu
  // (+ View Log ›) sits above one translucent card holding the type's
  // inputs, the "when" chips, and the grouped submit (Insulin owns its own
  // Save inside the card). MobileFrame's left nav rail + safe-area gutters
  // are untouched. The layout must fit the landscape floor with no scroll. ─
  if (mobileLandscape) {
    return (
      <div className={styles.lsRoot}>
        <div className={styles.lsMenuRow}>
          {segMenu}
          {offerRouteLinks && (
            <button
              type="button"
              className={styles.viewLogBtn}
              onClick={() => nav("/log/recent")}
            >
              View Log <span aria-hidden>›</span>
            </button>
          )}
        </div>
        <div className={`${styles.logFormCard} ${styles.logFormCardLs}`}>
          {mobileCardBody}
        </div>
        <Confetti burst={confettiBurst} />
        <CongratsModal
          message={congratsMsg}
          onDismiss={() => setCongratsMsg(null)}
        />
      </div>
    );
  }

  // ─── Cycle 30 → 327: mobile PORTRAIT branch ──────────────────────
  if (variant === "mobile") {
    return (
      <div className={styles.mobileRoot}>
        <div className={styles.mobileHeader}>
          <h2 className={styles.title}>Log</h2>
          {offerRouteLinks && (
            <button
              type="button"
              className={styles.viewLogBtn}
              onClick={() => nav("/log/recent")}
            >
              View Log <span aria-hidden>›</span>
            </button>
          )}
        </div>

        {/* Cycle 327 (deliverable 1): the type selector is the kiosk-style
            segmented menu with unit hints, sitting above the card (its own
            translucent segmented strip, so the card below isn't glass-on-
            glass). Replaces the cycle-323 `.mobileTabs`. */}
        {segMenu}

        {/* Cycle 327 (deliverable 2): the log form is ONE translucent glass
            card so the themed wallpaper reads through, and the primary submit
            CTA (+ its inline error/confirm) is grouped INSIDE it at the bottom
            of the pane. This reverses the cycle-323 near-opaque card +
            CTA-outside-the-card decisions (INTENTIONAL, operator-approved
            reversals). Insulin keeps its guided Save inside the card. */}
        <div className={styles.logFormCard}>{mobileCardBody}</div>

        {/* Cycle 34.18: celebratory confetti burst on every successful
            mobile log. The component is mount-cheap when `burst === 0`
            (renders null) and self-tears-down ~3 s after each burst. */}
        <Confetti burst={confettiBurst} />
        {/* Cycle 34.19: personalized "Congratulations!" modal — rides
            on top of the confetti for ~3.6 s, tap-outside dismisses
            early. Mobile-only consumer; kiosk + web log paths never
            mount it. */}
        <CongratsModal
          message={congratsMsg}
          onDismiss={() => setCongratsMsg(null)}
        />
      </div>
    );
  }

  return (
    <div
      className={`${styles.root} ${variant === "kiosk" ? styles.kiosk : ""} ${
        // Cycle 201: the Android-tablet kiosk Log home gets the glass
        // redesign (segmented selector, content-sized input card, glass
        // entries card). `nativeEntry` is the same Android-tablet-kiosk gate
        // cycle 199 uses; every cycle-201 rule is nested under this marker so
        // the Pi/iPad kiosk + web + mobile paths stay byte-identical.
        nativeEntry ? styles.androidTablet : ""
      }`}
    >
      <div className={styles.header}>
        <div style={{ display: "flex", alignItems: "baseline" }}>
          <h2 className={styles.title}>Log</h2>
          <span className={styles.titleSub}>
            {fmtTime(Date.now())}
          </span>
        </div>
        {offerRouteLinks && (
          <button
            type="button"
            className={styles.viewLogBtn}
            onClick={() =>
              // Cycle 203: on the Android-tablet kiosk View Log › lands
              // directly on the Calendar; every other surface keeps the flat
              // list. The /log/recent route's bare default (no ?view) is
              // unchanged — only this link target differs.
              nav(nativeEntry ? "/log/recent?view=calendar" : "/log/recent")
            }
          >
            View Log <span aria-hidden>›</span>
          </button>
        )}
      </div>

      {/* Cycle 201: on the Android-tablet kiosk the segmented selector
          spans the FULL width above both columns (its own grid row); the
          Pi/web kiosk keeps it inside the left column (byte-identical). The
          chip JSX is identical either way — only its placement differs. */}
      {nativeEntry && <div className={styles.tabsRow}>{kioskTabs}</div>}

      {/* Left column: tabs (non-tablet only) + entry */}
      <div className={styles.col}>
        {!nativeEntry && <div className={styles.tabsRow}>{kioskTabs}</div>}

        {/* Cycle 202: Android-tablet Carbs tab — a prominent glass CTA card
            at the top of the left column (📷 emoji, bold title, hint). Its
            tap fires the hidden file input INSIDE the user gesture, opening
            the (native-branded) chooser immediately — no dead landing page.
            The manual entry below sits behind a quiet "or enter manually"
            divider and stays fully functional. */}
        {nativeEntry && photoLineNode}
        {showTabletPhotoCta && (
          <>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              capture={mealCameraOnly ? "environment" : undefined}
              onChange={onTabletPhotoPicked}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className={styles.photoCta}
              onClick={openTabletPhotoChooser}
              disabled={photoBlocked}
            >
              <span className={styles.photoCtaIcon} aria-hidden>
                📷
              </span>
              <span className={styles.photoCtaText}>
                <span className={styles.photoCtaTitle}>Photo of meal</span>
                <span className={styles.photoCtaHint}>
                  {photoBlocked ? PHOTO_BLOCKED_LINE : "Snap it — we'll estimate the carbs"}
                </span>
              </span>
            </button>
            <div className={styles.orDivider}>
              <span>or enter manually</span>
            </div>
          </>
        )}

        <div className={styles.card}>
          {/* Cycle 126: Insulin → the guided flow (kiosk = two-pane
              controls|Numpad + Save beneath; web = controls + inline
              Numpad + Save). It owns its own "when" control + Save, so
              the generic entry below is skipped for the Insulin tab. */}
          {isInsulin ? (
            insulinFlow
          ) : (
            <>
          {/* Cycle 58: kiosk renders label + value readout on one 75/25
              line, then the full-width numpad key grid below (Numpad's
              own built-in display is suppressed via hideDisplay so the
              25% readout isn't duplicated). Web (desktop) keeps the
              cycle-pre-58 stacked label-then-numpad-with-display layout. */}
          {/* Cycle 199: Android-tablet kiosk → the MOBILE native-input
              path (label input + native <input inputmode> → OS number
              pad). No readout button, no slide-in overlay. */}
          {/* Cycle 201: the food/name field + the amount field share ONE
              row (75/25), matching the approved mockup. */}
          {useNumpad && nativeEntry && (
            <div className={entryRowClass}>
              <input
                type="text"
                className={styles.labelInput}
                placeholder={LABEL_PLACEHOLDER[kind as Exclude<Kind, "note">]}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={120}
              />
              {kind === "exercise" || kind === "carbs" || kind === "bg_check"
                ? tabletUnitField
                : mobileNumericInput}
            </div>
          )}
          {/* Cycle 58/133: PI kiosk only — label + readout-button on one
              75/25 row; the readout slides the numpad overlay in over
              Today's entries. */}
          {useNumpad && variant === "kiosk" && !nativeEntry && (
            <div className={entryRowClass}>
              <input
                type="text"
                className={styles.labelInput}
                placeholder={LABEL_PLACEHOLDER[kind as Exclude<Kind, "note">]}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={120}
              />
              {/* Cycle 133: the readout is now a button that slides the
                  numpad overlay in over Today's entries. */}
              <button
                type="button"
                className={`${styles.kioskReadout} ${
                  kioskPadOpen ? styles.kioskReadoutActive : ""
                }`}
                onClick={() => setKioskPadOpen(true)}
                aria-label={`Enter ${kindLabel(kind)}`}
              >
                <span
                  className={`${styles.kioskReadoutValue} ${
                    value === "" ? styles.kioskReadoutPlaceholder : ""
                  }`}
                >
                  {value === "" ? "—" : value}
                </span>
                {kindUnit(kind, units) ? (
                  <span className={styles.kioskReadoutUnit}>
                    {kindUnit(kind, units)}
                  </span>
                ) : null}
              </button>
            </div>
          )}
          {useNumpad && variant !== "kiosk" && (
            <input
              type="text"
              className={styles.labelInput}
              placeholder={LABEL_PLACEHOLDER[kind as Exclude<Kind, "note">]}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={120}
            />
          )}
          {!nativeEntry && photoLineNode}
          {showPhotoButton && !nativeEntry && (
            // Cycle 340: two explicit targets — Take Photo opens the camera
            // directly, Choose from Library opens the gallery directly.
            // Each fires its hidden input inside this click and hands the
            // File straight to PhotoMealScreen (no /log/photo landing
            // screen, no second click there). The Android-tablet kiosk uses
            // its own CTA card above (nativeEntry).
            <div className={styles.photoCtaWebGroup}>
              <button
                type="button"
                className={styles.photoCtaWeb}
                onClick={openCamera}
              >
                <span className={styles.photoCtaWebIcon} aria-hidden>
                  📷
                </span>
                Take Photo
              </button>
              {mealCameraOnly ? null : (
                <button
                  type="button"
                  className={styles.photoCtaWebSecondary}
                  onClick={openGallery}
                >
                  <span aria-hidden>🖼️</span>
                  Choose from Library
                </button>
              )}
              <input
                ref={cameraInputRef}
                type="file"
                accept={ACCEPT_IMAGE_CAMERA}
                capture="environment"
                onChange={onDirectPhotoPicked}
                style={{ display: "none" }}
              />
              {mealCameraOnly ? null : (
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept={ACCEPT_IMAGE_GALLERY}
                  onChange={onDirectPhotoPicked}
                  style={{ display: "none" }}
                />
              )}
            </div>
          )}
          {/* Cycle 293: web numeric entry is a native keyboard <input>, not
              the custom Numpad. The Pi kiosk numpad is a slide-in overlay
              over Today's entries (rendered in the right column below). */}
          {useNumpad && variant !== "kiosk" && webNumericInput}
          {noteSection && (
            <textarea
              className={styles.notes}
              placeholder="What happened?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}

          {/* Timestamp picker — cycle 57.11: pill row replaced with a
              dropdown so the left column reclaims a row of vertical
              budget on the 1024×600 kiosk. */}
          <label className={styles.timeSelectRow}>
            <span className={styles.timeSelectLabel}>When</span>
            <select
              value={tsChip}
              onChange={(e) => setTsChip(e.target.value)}
              className={styles.timeSelect}
              aria-label="When"
            >
              {TIME_OFFSETS_MIN.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
            {tsChip === "pick" && (
              <input
                type="datetime-local"
                value={pickedAt}
                onChange={(e) => setPickedAt(e.target.value)}
                className={styles.timeInput}
              />
            )}
          </label>

          {error && <div className={styles.errorMsg}>{error}</div>}
          {confirm && (
            <Pill tone="ok" role="status">
              {confirm}
            </Pill>
          )}

          {/* Cycle 57.10: kiosk Log Carbs button trimmed via the
              scoped .submitKiosk class — frees vertical real estate
              for the bumped time-chip strip in the left column. */}
          <Button
            onClick={submit}
            disabled={busy}
            full
            className={variant === "kiosk" ? styles.submitKiosk : undefined}
          >
            {busy ? "Logging…" : `Log ${kindLabel(kind)}`}
          </Button>
            </>
          )}
        </div>
      </div>

      {/* Right column: recent entries (cycle 34 on kiosk: today's
          entries only, filling the column top-to-bottom). Cycle 133: on
          the kiosk this card is the host for the slide-in numpad overlay
          (rendered over the entries while a numeric field is entered). */}
      <div className={`${styles.col} ${styles.rightCol}`}>
        <div className={`${styles.card} ${styles.rightCard}`}>
          {variant === "kiosk" ? (
            <div className={styles.kioskPadHost} ref={setPadHost} />
          ) : null}
          {variant === "kiosk" && !isInsulin && useNumpad && !nativeEntry ? (
            <KioskNumpadOverlay
              open={kioskPadOpen}
              label={kindLabel(kind)}
              value={value}
              unit={kindUnit(kind, units)}
              onChange={setValue}
              allowDecimal={kind !== "bg_check" && kind !== "exercise"}
              maxLength={kind === "bg_check" ? 3 : 5}
              onDone={() => setKioskPadOpen(false)}
              portalTarget={padHost}
            />
          ) : null}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <span
              style={{
                fontSize: "var(--type-floor)",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--ink-3)",
              }}
            >
              {variant === "kiosk" ? "Today's Entries" : "Recent (24h)"}
            </span>
            <div
              style={{ display: "flex", alignItems: "baseline", gap: 10 }}
            >
              <span style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)" }}>
                {variant === "kiosk"
                  ? `${visibleEntries.length} ${
                      visibleEntries.length === 1 ? "entry" : "entries"
                    } today`
                  : `${visibleEntries.length} ${
                      visibleEntries.length === 1 ? "entry" : "entries"
                    }`}
              </span>
              {/* Cycle 42: reach the 30-day notes "mini case studies"
                  surface from the kiosk's primary log view. */}
              {offerRouteLinks && (
                <button
                  type="button"
                  className={styles.mobileRecentLink}
                  onClick={() => nav("/log/recent?kind=note")}
                >
                  Notes <span aria-hidden>›</span>
                </button>
              )}
            </div>
          </div>
          {visibleEntries.length === 0 ? (
            <div style={{ color: "var(--ink-3)", fontSize: 13, padding: "8px 0" }}>
              {variant === "kiosk" ? "Nothing logged today." : "No entries yet."}
            </div>
          ) : (
            <div className={styles.recent}>
              {visibleEntries.map((tr) => {
                const v =
                  tr.event_type === "carbs"
                    ? `${tr.carbs_g ?? "—"} g`
                    : tr.event_type === "insulin"
                      ? `${tr.insulin_u ?? "—"} u`
                      : "";
                return (
                  <div
                    key={tr.id}
                    className={`${styles.entry} ${styles.entryClickable}`}
                    // Cycle 201: kind-colored tag on the Android-tablet kiosk
                    // only — the per-row color comes from the existing
                    // treatmentColor() token source (no new hex), consumed by
                    // `.androidTablet .entryKind` via this CSS variable. Other
                    // paths render no style attr (byte-identical).
                    style={
                      nativeEntry
                        ? ({
                            ["--entry-color"]: treatmentColor(tr.event_type),
                          } as CSSProperties)
                        : undefined
                    }
                    role="button"
                    tabIndex={0}
                    onClick={() => setViewingRow(tr)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setViewingRow(tr);
                      }
                    }}
                  >
                    <span className={styles.entryKind}>
                      {tr.event_type.replace("_", " ")}
                    </span>
                    {v && <span className={styles.entryValue}>{v}</span>}
                    <span className={styles.entryNote}>
                      {tr.notes ?? ""}
                    </span>
                    <span className={styles.entryTime}>
                      {fmtEntryTime(tr.timestamp, Date.now(), h24)}
                    </span>
                    <button
                      type="button"
                      className={styles.entryEdit}
                      title="Edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingRow(tr);
                      }}
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className={styles.entryDel}
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete(tr);
                      }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <EditTreatmentModal
        treatment={editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={() => {
          void reloadRecent();
        }}
      />
      <ViewEntryModal
        treatment={viewingRow}
        onClose={() => setViewingRow(null)}
        h24={h24}
        units={units}
      />
      <ConfirmDeleteModal
        treatment={pendingDelete}
        h24={h24}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          const target = pendingDelete;
          setPendingDelete(null);
          if (target) void removeOne(target.id);
        }}
      />
      {/* Cycle 203: confetti + congrats on the Android-tablet kiosk log path
          (mobile mounts its own in the branches above). nativeEntry-gated so
          the Pi/iPad/web kiosk never instantiate them → non-celebrating. */}
      {nativeEntry && (
        <>
          <Confetti burst={confettiBurst} />
          <CongratsModal
            message={congratsMsg}
            onDismiss={() => setCongratsMsg(null)}
          />
        </>
      )}
    </div>
  );
}
